(() => {
  'use strict';

  const NAV_SELECTOR = '[data-dl-desktop-nav]';
  const ITEM_SELECTOR = '[data-dl-nav-item]';
  const TRIGGER_SELECTOR = '[data-dl-nav-trigger]';
  const PANEL_SELECTOR = '[data-dl-mega-panel]';
  const SAVED_WATCH_KEY = 'daluca_selected_watch_v1';
  const OPEN_DELAY = 130;
  const CLOSE_DELAY = 260;

  function initDesktopNav() {
    const nav = document.querySelector(NAV_SELECTOR);
    if (!nav) return;

    const items = Array.from(nav.querySelectorAll(ITEM_SELECTOR));
    let activeItem = null;
    let openTimer = null;
    let closeTimer = null;

    const clearTimers = () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      openTimer = null;
      closeTimer = null;
    };

    const closeItem = (item, restoreFocus = false) => {
      if (!item) return;
      const trigger = item.querySelector(TRIGGER_SELECTOR);
      const panel = item.querySelector(PANEL_SELECTOR);
      if (!trigger || !panel) return;
      trigger.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      panel.classList.remove('is-opening');
      item.classList.remove('is-open');
      if (activeItem === item) activeItem = null;
      nav.classList.toggle('is-expanded', !!activeItem);
      if (restoreFocus) trigger.focus({ preventScroll: true });
    };

    const closeAll = (except = null) => {
      items.forEach((item) => {
        if (item !== except) closeItem(item);
      });
    };

    const openItem = (item, focusFirst = false) => {
      if (!item || window.matchMedia('(max-width: 1067px)').matches) return;
      const trigger = item.querySelector(TRIGGER_SELECTOR);
      const panel = item.querySelector(PANEL_SELECTOR);
      if (!trigger || !panel) return;

      closeAll(item);
      panel.hidden = false;
      panel.classList.remove('is-opening');
      void panel.offsetWidth;
      panel.classList.add('is-opening');
      trigger.setAttribute('aria-expanded', 'true');
      item.classList.add('is-open');
      activeItem = item;
      nav.classList.add('is-expanded');

      document.dispatchEvent(new CustomEvent('daluca:mega-opened', {
        detail: {
          section: item.getAttribute('data-nav-section') || '',
          label: (trigger.getAttribute('data-dl-nav-label') || window.DaLucaI18n.sourceText(trigger) || '').trim().replace(/\s+/g, ' ')
        }
      }));

      if (focusFirst) {
        const firstLink = panel.querySelector('a[href], button:not([disabled])');
        if (firstLink) firstLink.focus({ preventScroll: true });
      }
    };

    items.forEach((item, itemIndex) => {
      const trigger = item.querySelector(TRIGGER_SELECTOR);
      const panel = item.querySelector(PANEL_SELECTOR);
      if (!trigger || !panel) return;

      /* V52 accessibility hardening: explicitly associate every top-level
         trigger with the panel it controls. Preserve any theme-provided id. */
      if (!panel.id) panel.id = `dl-mega-panel-${itemIndex + 1}`;
      trigger.setAttribute('aria-controls', panel.id);
      if (!trigger.hasAttribute('aria-expanded')) {
        trigger.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
      }

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        clearTimers();
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (isOpen) closeItem(item);
        else openItem(item);
      });

      const destination = item.querySelector('.dl-nav-category-link');
      destination?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          clearTimers();
          openItem(item, true);
        }
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          clearTimers();
          openItem(item, true);
        }
      });

      /* V48: deliberate desktop hover behavior. A short opening delay avoids
         accidental fly-outs; the longer close grace period lets the pointer
         travel diagonally into the full-width panel without it disappearing. */
      item.addEventListener('mouseenter', () => {
        if (!window.matchMedia('(min-width: 1068px) and (hover: hover) and (pointer: fine)').matches) return;
        clearTimers();
        openTimer = window.setTimeout(() => openItem(item), OPEN_DELAY);
      });

      item.addEventListener('mouseleave', () => {
        if (!window.matchMedia('(min-width: 1068px) and (hover: hover) and (pointer: fine)').matches) return;
        clearTimers();
        closeTimer = window.setTimeout(() => closeItem(item), CLOSE_DELAY);
      });

      item.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!item.contains(document.activeElement)) closeItem(item);
        }, 0);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && activeItem) {
        event.preventDefault();
        const item = activeItem;
        closeItem(item, true);
      }
    });

    document.addEventListener('click', (event) => {
      if (activeItem && !nav.contains(event.target)) closeAll();
    });

    window.addEventListener('resize', () => {
      if (window.matchMedia('(max-width: 1067px)').matches) closeAll();
    }, { passive: true });
  }

  function markActiveSection() {
    const path = (window.location.pathname || '/').toLowerCase();
    let section = '';

    if (path.includes('/pages/strap-finder')) {
      section = 'strap-finder';
    } else if (path.includes('/collections/custom-')) {
      section = 'custom';
    } else if (
      path.includes('/collections/rolex-') ||
      path.includes('/collections/omega-') ||
      path.includes('/collections/tudor-') ||
      path.includes('/collections/panerai-') ||
      path.includes('-panerai-') ||
      path.includes('/collections/breitling-') ||
      path.includes('/collections/iwc-') ||
      path.includes('/collections/seiko-') ||
      path.includes('/collections/grand-seiko-')
    ) {
      section = 'shop-by-watch';
    } else if (
      path.includes('/collections/accessories') ||
      path.includes('/collections/wallets') ||
      path.includes('/collections/key-chains') ||
      path.includes('/collections/handmade-leather-belts') ||
      path.includes('/collections/leather-care') ||
      path.includes('/products/the-tray')
    ) {
      section = 'accessories';
    } else if (
      path.includes('/pages/about-us') ||
      path.includes('/pages/press-media') ||
      path.includes('/pages/testimonials') ||
      path.includes('/pages/stockists') ||
      path.includes('/pages/contact-us') ||
      path.includes('/pages/help') ||
      path.includes('/pages/shipping-returns') ||
      path.includes('/blogs/')
    ) {
      section = 'about';
    } else if (path.includes('/collections/custom-watch-straps')) {
      section = 'custom';
    } else if (path.includes('/collections/')) {
      section = 'watch-straps';
    }

    if (!section) return;
    document.querySelectorAll(`[data-nav-section="${section}"]`).forEach((item) => {
      item.classList.add('is-active');
    });
  }

  function formatSavedWatch(watch) {
    if (!watch || !watch.brand || !Number(watch.lug_width_mm)) return '';
    const bits = [];
    const brand = String(watch.brand || '').trim();
    const model = String(watch.model || '').trim();
    const reference = String(watch.reference || watch.canonical_reference || '').trim();

    let main = [brand, model].filter(Boolean).join(' ');
    if (reference && !main.toLowerCase().includes(reference.toLowerCase())) {
      main += ` ${reference}`;
    }
    if (main) bits.push(main);
    bits.push(`${Number(watch.lug_width_mm)}mm`);
    return bits.join(' · ');
  }

  function savedWatchDestination(watch) {
    const width = Number(watch && watch.lug_width_mm);
    if (!width) return { href: window.DaLucaI18n.url('/pages/strap-finder'), label: window.DaLucaI18n.t("daluca_nav.find_exact_fit", "Find exact fit") };

    const readyMade = new Set([18, 20, 22, 24, 26]);
    const customOnly = !!(
      watch.custom_route ||
      watch.custom_route_width_mm ||
      !readyMade.has(width)
    );

    if (customOnly) {
      return {
        href: window.DaLucaI18n.url('/collections/custom-watch-straps'),
        label: window.DaLucaI18n.t("daluca_nav.shop_width_mm_custom", "Shop {{ width }}mm custom", { width })
      };
    }

    return {
      href: window.DaLucaI18n.url(`/collections/${width}mm-watch-straps`),
      label: window.DaLucaI18n.t("daluca_nav.shop_width_mm_straps", "Shop {{ width }}mm straps", { width })
    };
  }

  function initSavedWatchMemory() {
    const containers = Array.from(document.querySelectorAll(
      '[data-dl-watch-memory], [data-dl-watch-memory-mobile], [data-dl-watch-memory-inline]'
    ));
    const emptyStates = Array.from(document.querySelectorAll('[data-dl-watch-memory-empty]'));
    if (!containers.length && !emptyStates.length) return;

    let lastRaw;

    const update = () => {
      let raw = null;
      try { raw = window.DaLucaSelectedWatchMemory ? JSON.stringify(window.DaLucaSelectedWatchMemory) : window.localStorage.getItem(SAVED_WATCH_KEY); } catch (_) { raw = window.DaLucaSelectedWatchMemory ? JSON.stringify(window.DaLucaSelectedWatchMemory) : null; }
      if (raw === lastRaw) return;
      lastRaw = raw;

      let watch = null;
      let label = '';
      if (raw) {
        try {
          watch = JSON.parse(raw);
          label = formatSavedWatch(watch);
        } catch (_) {
          watch = null;
          label = '';
        }
      }

      const destination = watch ? savedWatchDestination(watch) : null;

      containers.forEach((container) => {
        container.querySelectorAll('[data-dl-watch-memory-text]').forEach((node) => {
          window.DaLucaI18n.setText(node, label);
        });

        container.querySelectorAll('[data-dl-watch-memory-shop]').forEach((link) => {
          if (!destination) return;
          link.href = destination.href;
          window.DaLucaI18n.setText(link, destination.label);
        });

        container.hidden = !label;
      });

      emptyStates.forEach((container) => {
        container.hidden = !!label;
      });
    };

    update();
    document.addEventListener('daluca:selected-watch-changed', update);
    window.addEventListener('storage', (event) => {
      if (event.key === SAVED_WATCH_KEY || event.key === null) { window.DaLucaSelectedWatchMemory = null; update(); }
    });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) update();
    });
    window.setInterval(update, 1500);
  }

  function markCurrentLinks() {
    const currentPath = (window.location.pathname || '/').replace(/\/$/, '') || '/';
    const currentSearch = window.location.search || '';

    document.querySelectorAll('.dl-navsys a[href], .dl-mobile-menu-drawer a[href]').forEach((link) => {
      let url;
      try { url = new URL(link.href, window.location.origin); } catch (_) { return; }
      const linkPath = (url.pathname || '/').replace(/\/$/, '') || '/';
      const pathMatch = linkPath === currentPath;
      const searchMatch = !url.search || url.search === currentSearch;
      if (pathMatch && searchMatch) {
        link.classList.add('is-current');
        link.setAttribute('aria-current', 'page');
      }
    });
  }

  function initNavigationTelemetry() {
    const clean = (value) => String(value || '').trim().replace(/\s+/g, ' ');

    const contextFor = (node) => {
      const item = node && node.closest && node.closest('[data-dl-nav-item]');
      const panel = node && node.closest && node.closest('[data-dl-mega-panel]');
      const rail = node && node.closest && node.closest('.dl-v20-stage');
      return {
        section: (item && item.getAttribute('data-nav-section')) || '',
        panel_open: !!panel,
        visual_menu: !!rail,
        mobile: !!(node && node.closest && node.closest('.dl-mobile-menu-drawer'))
      };
    };

    const emit = (eventName, detail) => {
      const payload = Object.assign({ event_name: eventName }, detail || {});
      window.dispatchEvent(new CustomEvent(`daluca:${eventName}`, { detail: payload }));

      /* GTM / GA4-ready data layer. Safe no-op on stores without GTM. */
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    };

    document.addEventListener('click', (event) => {
      const link = event.target.closest('.dl-header-shell a[href], .dl-mobile-menu-drawer a[href]');
      if (link) {
        const ctx = contextFor(link);
        emit('daluca_nav_click', Object.assign(ctx, {
          label: clean(window.DaLucaI18n.sourceText(link)),
          href: link.getAttribute('href') || '',
          current_path: window.location.pathname || '/'
        }));
        return;
      }

      const choice = event.target.closest('.dl-v20-rail-btn, .dl-v20-width-tabs button, [data-dl-switcher-tab], [data-dl-sub-tab]');
      if (!choice) return;
      const ctx = contextFor(choice);
      emit('daluca_nav_choice', Object.assign(ctx, {
        label: clean(window.DaLucaI18n.sourceText(choice)),
        choice_type: choice.matches('.dl-v20-width-tabs button') ? 'width' :
          choice.matches('.dl-v20-rail-btn') ? 'section' :
          choice.hasAttribute('data-dl-sub-tab') ? 'subsection' : 'tab'
      }));
    }, true);

    document.addEventListener('daluca:mega-opened', (event) => {
      const detail = event && event.detail ? event.detail : {};
      emit('daluca_mega_open', {
        section: detail.section || '',
        label: clean(detail.label),
        mobile: false
      });
    });
  }

  function init() {
    initDesktopNav();
    markActiveSection();
    initSavedWatchMemory();
    markCurrentLinks();
    initNavigationTelemetry();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

/* =========================================================
   DALUCA NAVIGATION SYSTEM V7 — CONTEXTUAL SWITCHING
   ========================================================= */
(() => {
  'use strict';

  function activate(root, tab, tabSelector, paneSelector, keyAttr, paneAttr) {
    if (!root || !tab) return;
    const key = tab.getAttribute(keyAttr);
    if (!key) return;

    root.querySelectorAll(tabSelector).forEach((item) => {
      const active = item === tab;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    root.querySelectorAll(paneSelector).forEach((pane) => {
      const active = pane.getAttribute(paneAttr) === key;
      pane.hidden = !active;
      pane.classList.toggle('is-active', active);
    });
  }

  function activateMain(root, key) {
    if (!root || !key) return;
    const tab = Array.from(root.querySelectorAll('[data-dl-switcher-tab]')).find(
      (item) => item.getAttribute('data-dl-switcher-tab') === key
    );
    if (tab) activate(root, tab, '[data-dl-switcher-tab]', '[data-dl-switcher-pane]', 'data-dl-switcher-tab', 'data-dl-switcher-pane');
  }

  function activateSub(root, key) {
    if (!root || !key) return;
    const tab = Array.from(root.querySelectorAll('[data-dl-sub-tab]')).find(
      (item) => item.getAttribute('data-dl-sub-tab') === String(key)
    );
    if (tab) activate(root, tab, '[data-dl-sub-tab]', '[data-dl-sub-pane]', 'data-dl-sub-tab', 'data-dl-sub-pane');
  }

  function initSwitchers() {
    document.querySelectorAll('[data-dl-switcher]').forEach((root) => {
      root.querySelectorAll('[data-dl-switcher-tab]').forEach((tab) => {
        tab.addEventListener('mouseenter', () => {
          if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
            activate(root, tab, '[data-dl-switcher-tab]', '[data-dl-switcher-pane]', 'data-dl-switcher-tab', 'data-dl-switcher-pane');
          }
        });
        tab.addEventListener('click', () => {
          activate(root, tab, '[data-dl-switcher-tab]', '[data-dl-switcher-pane]', 'data-dl-switcher-tab', 'data-dl-switcher-pane');
        });
      });
    });

    document.querySelectorAll('[data-dl-sub-switcher]').forEach((root) => {
      root.querySelectorAll('[data-dl-sub-tab]').forEach((tab) => {
        const run = () => activate(root, tab, '[data-dl-sub-tab]', '[data-dl-sub-pane]', 'data-dl-sub-tab', 'data-dl-sub-pane');
        tab.addEventListener('mouseenter', () => {
          if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) run();
        });
        tab.addEventListener('click', run);
      });
    });
  }

  function savedBrand() {
    try {
      const raw = window.localStorage.getItem('daluca_selected_watch_v1');
      if (!raw) return '';
      const watch = JSON.parse(raw);
      return String((watch && (watch.brand_id || watch.brand)) || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    } catch (_) {
      return '';
    }
  }

  function initContext() {
    const path = (window.location.pathname || '/').toLowerCase();

    const watchRoot = document.querySelector('[data-dl-context-root="watch-straps"]');
    if (watchRoot) {
      const widthMatch = path.match(/\/collections\/(18|20|22|24|26)mm(?:-|\/|$)/);
      if (widthMatch) {
        activateMain(watchRoot, 'width');
        activateSub(watchRoot.querySelector('[data-dl-sub-switcher]'), widthMatch[1]);
      } else if (
        path.includes('shell-cordovan') ||
        path.includes('vintage') ||
        path.includes('suede') ||
        path.includes('nylon') ||
        path.includes('rubber') ||
        path.includes('perlon') ||
        path.includes('/collections/leather-watch-straps')
      ) {
        activateMain(watchRoot, 'material');
      }
    }

    const watchBrandRoot = document.querySelector('[data-dl-context-root="shop-by-watch"]');
    if (watchBrandRoot) {
      let brand = '';
      ['rolex', 'omega', 'panerai', 'tudor', 'iwc', 'apple'].some((candidate) => {
        if (path.includes(candidate)) {
          brand = candidate;
          return true;
        }
        return false;
      });

      if (!brand) {
        const remembered = savedBrand();
        if (remembered.includes('rolex')) brand = 'rolex';
        else if (remembered.includes('omega')) brand = 'omega';
        else if (remembered.includes('panerai')) brand = 'panerai';
        else if (remembered.includes('tudor')) brand = 'tudor';
        else if (remembered.includes('iwc')) brand = 'iwc';
        else if (remembered.includes('apple')) brand = 'apple';
      }

      if (brand) activateMain(watchBrandRoot, brand);
    }

    const customRoot = document.querySelector('[data-dl-context-root="custom"]');
    if (customRoot && path.includes('/collections/custom-')) {
      const watchSpecific = ['rolex', 'panerai', 'omega', 'tudor', 'iwc'].some((brand) => path.includes(brand));
      activateMain(customRoot, watchSpecific ? 'custom-watch' : 'custom-material');
    }
  }

  function init() {
    initSwitchers();
    initContext();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();


/* =========================================================
   DALUCA NAV V9 — INDUSTRIAL FILTER-BAR CONTROLS
   Header-only enhancement. No filter assets are touched.
   ========================================================= */
(() => {
  'use strict';

  function closeDesktopNav() {
    const nav = document.querySelector('[data-dl-desktop-nav]');
    if (!nav) return;
    const openItem = nav.querySelector('[data-dl-nav-item].is-open');
    if (!openItem) return;
    const trigger = openItem.querySelector('[data-dl-nav-trigger]');
    const panel = openItem.querySelector('[data-dl-mega-panel]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) {
      panel.hidden = true;
      panel.classList.remove('is-opening');
    }
    openItem.classList.remove('is-open');
    nav.classList.remove('is-expanded');
    if (trigger) trigger.focus({ preventScroll: true });
  }

  function installPanelCloseControls() {
    document.querySelectorAll('[data-dl-mega-panel] .dl-navsys__toolbar').forEach((toolbar) => {
      if (toolbar.querySelector('.dl-navsys-close')) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dl-navsys-close';
      button.setAttribute('aria-label', 'Close navigation');
      window.DaLucaI18n.setHtml(button, '<span aria-hidden="true"><i></i><i></i></span>');
      button.addEventListener('click', (event) => {
        event.preventDefault();
        closeDesktopNav();
      });
      toolbar.appendChild(button);
    });
  }

  function installMobileHeader() {
    const content = document.querySelector('.dl-mobile-menu-content');
    const drawer = document.querySelector('.dl-mobile-menu-drawer');
    const wrapper = document.querySelector('.dl-mobile-toggle-wrapper');
    if (!content || !drawer || !wrapper || content.querySelector('.dl-mobile-nav-head')) return;

    const head = document.createElement('div');
    head.className = 'dl-mobile-nav-head';
    window.DaLucaI18n.setHtml(head, `
      <span class="dl-mobile-nav-head__title">
        <span class="dl-mobile-nav-head__filter-icon" aria-hidden="true"><i></i><i></i><i></i></span>
        Navigation
      </span>
      <button class="dl-mobile-nav-head__close" type="button" aria-label="Close navigation">
        <span aria-hidden="true"><i></i><i></i></span>
      </button>`);
    content.prepend(head);

    const close = head.querySelector('.dl-mobile-nav-head__close');
    close.addEventListener('click', () => {
      wrapper.removeAttribute('open');
      const summary = wrapper.querySelector('summary');
      if (summary) summary.focus({ preventScroll: true });
    });
  }

  function quietFinderPromo() {
    document.querySelectorAll('.dl-mobile-finder-cta').forEach((node) => {
      node.setAttribute('hidden', '');
    });

    document.querySelectorAll('.dl-mobile-utility').forEach((utility) => {
      const saved = Array.from(utility.querySelectorAll('.dl-mobile-saved-watch'));
      const sync = () => {
        utility.hidden = !saved.some((card) => !card.hidden);
      };
      sync();
      const observer = new MutationObserver(sync);
      saved.forEach((card) => observer.observe(card, { attributes: true, attributeFilter: ['hidden'] }));
    });
  }

  function initV9() {
    installPanelCloseControls();
    installMobileHeader();
    quietFinderPromo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initV9, { once: true });
  } else {
    initV9();
  }
})();

/* =========================================================
   DALUCA NAV V10 — MASTER CHEVRON CONTROL
   The arrow is now a real control:
   - closed -> opens Watch Straps
   - open   -> closes the active panel
   Category clicks keep the panel open and switch the underline.
   ========================================================= */
(() => {
  'use strict';

  function initMasterToggle() {
    const nav = document.querySelector('[data-dl-desktop-nav]');
    if (!nav || nav.querySelector('.dl-nav-master-toggle')) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'dl-nav-master-toggle';
    button.setAttribute('aria-label', 'Open navigation');
    button.setAttribute('aria-expanded', 'false');
    nav.appendChild(button);

    const sync = () => {
      const expanded = nav.classList.contains('is-expanded');
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      button.setAttribute('aria-label', expanded ? 'Close navigation' : 'Open navigation');
    };

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const openItem = nav.querySelector('[data-dl-nav-item].is-open');
      if (openItem) {
        const openTrigger = openItem.querySelector('[data-dl-nav-trigger]');
        if (openTrigger) openTrigger.click();
        return;
      }

      const watchItem = nav.querySelector('[data-nav-section="watch-straps"]');
      const watchTrigger = watchItem && watchItem.querySelector('[data-dl-nav-trigger]');
      if (watchTrigger) watchTrigger.click();
    });

    new MutationObserver(sync).observe(nav, {
      attributes: true,
      attributeFilter: ['class']
    });

    sync();
  }

  function initV10() {
    initMasterToggle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initV10, { once: true });
  } else {
    initV10();
  }
})();

/* =========================================================
   DALUCA NAV V11 — SMART NAV SEARCH + PANEL CHROME
   Search layers:
   1) server-rendered SEO navigation links
   2) Shopify predictive search (products / collections / pages)
   3) exact watch/reference matches from the same V20 production DB
   The large watch DB is fetched lazily only for watch-like queries.
   ========================================================= */
(() => {
  'use strict';

  const WATCH_DB_URL = 'https://cdn.shopify.com/s/files/1/0857/0362/2962/files/daluca-watch-db-production-v20-CURATED.json';
  const WATCH_FINDER_URL = window.DaLucaI18n.url('/pages/strap-finder');
  const READY_WIDTHS = new Set([18, 20, 22, 24, 26]);
  const PANEL_NOTES = {
    'dl-mega-watch-straps': window.DaLucaI18n.t("daluca_nav.handmade_straps_by_type_material_and_exact_fit", "Handmade straps by type, material and exact fit"),
    'dl-mega-shop-by-watch': window.DaLucaI18n.t("daluca_nav.find_the_exact_fit_for_your_watch", "Find the exact fit for your watch"),
    'dl-mega-custom': window.DaLucaI18n.t("daluca_nav.made_to_order_for_your_watch", "Made to order for your watch"),
    'dl-mega-accessories': window.DaLucaI18n.t("daluca_nav.leather_goods_tools_and_desk_pieces", "Leather goods, tools and desk pieces")
  };

  let watchDbPromise = null;
  let predictiveTimer = null;
  let requestSequence = 0;

  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

  const compact = (value) => normalize(value).replace(/\s+/g, '');

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  function ensurePanelNotes() {
    document.querySelectorAll('[data-dl-mega-panel]').forEach((panel) => {
      const toolbar = panel.querySelector('.dl-navsys__toolbar');
      if (!toolbar) return;
      const id = panel.id || '';
      const noteText = PANEL_NOTES[id];
      if (!noteText) return;

      let note = toolbar.querySelector('.dl-navsys__toolbar-note, .dl-navsys__section-note');
      if (!note) {
        note = document.createElement('span');
        note.className = 'dl-navsys__section-note';
        const micro = toolbar.querySelector('.dl-navsys__micro');
        if (micro) micro.insertAdjacentElement('afterend', note);
        else toolbar.prepend(note);
      }
      window.DaLucaI18n.setText(note, noteText);
    });
  }

  function buildNavIndex(nav) {
    const seen = new Set();
    const items = [];

    nav.querySelectorAll('[data-dl-mega-panel] a[href]').forEach((link) => {
      const href = link.getAttribute('href') || '';
      if (!href || href === '#') return;
      const title = (window.DaLucaI18n.sourceText(link) || '').trim().replace(/\s+/g, ' ');
      if (!title || title.length > 130) return;
      const panel = link.closest('[data-dl-mega-panel]');
      const section = panel ? (window.DaLucaI18n.chainText(() => (panel), [{kind:"member", optional:false, computed:false, key: () => ("querySelector")}, {kind:"call", optional:false, args: () => ['.dl-navsys__micro']}, {kind:"text", optional:true}]) || 'DaLuca').trim() : 'DaLuca';
      const key = `${href}|${title}`.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      const haystack = normalize(`${title} ${section} ${href.replace(/[\/_-]+/g, ' ')}`);
      items.push({ title, href, section, haystack });
    });

    return items;
  }

  function scoreNavItem(item, rawQuery) {
    const q = normalize(rawQuery);
    const qc = compact(rawQuery);
    if (!q) return 0;

    const title = normalize(item.title);
    const titleCompact = compact(item.title);
    const haystack = item.haystack;
    let score = 0;

    if (title === q) score += 9000;
    else if (title.startsWith(q)) score += 5200;
    else if (title.includes(q)) score += 3300;

    if (qc && titleCompact === qc) score += 5000;
    else if (qc && titleCompact.startsWith(qc)) score += 2600;
    else if (qc && titleCompact.includes(qc)) score += 1500;

    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length && tokens.every((token) => haystack.includes(token))) score += 2100;
    if (item.href.includes('/collections/')) score += 90;
    return score;
  }

  function localNavResults(navIndex, query, limit = 5) {
    return navIndex
      .map((item) => ({ ...item, score: scoreNavItem(item, query) }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title))
      .slice(0, limit)
      .map((item) => ({
        type: 'COLLECTION',
        title: item.title,
        subtitle: item.section,
        url: item.href,
        meta: ''
      }));
  }

  function shouldSearchWatchDb(query) {
    const q = normalize(query);
    if (q.length < 3) return false;
    const hasDigits = /\d{3,}/.test(compact(query));
    const watchWords = /\b(rolex|omega|panerai|tudor|iwc|breitling|seiko|grand seiko|longines|oris|sinn|nomos|hamilton|tissot|zenith|patek|marathon|submariner|speedmaster|seamaster|luminor|radiomir|pelagos|datejust|daytona|explorer|gmt)\b/i.test(query);
    return hasDigits || watchWords;
  }

  async function loadWatchDb() {
    if (watchDbPromise) return watchDbPromise;
    watchDbPromise = fetch(WATCH_DB_URL, { credentials: 'omit', cache: 'force-cache' })
      .then((response) => {
        if (!response.ok) throw new Error(`Watch DB ${response.status}`);
        return response.json();
      })
      .then((data) => Array.isArray(data) ? data : (Array.isArray(data.records) ? data.records : []))
      .catch(() => []);
    return watchDbPromise;
  }

  function watchSearchText(record) {
    return [
      record.brand,
      record.display_model,
      record.model,
      record.reference,
      record.canonical_reference,
      record.display_reference,
      ...(record.aliases || []),
      ...(record.search_terms || [])
    ].filter(Boolean).join(' ');
  }

  function scoreWatch(record, rawQuery) {
    const q = normalize(rawQuery);
    const qc = compact(rawQuery);
    const ref = compact(record.canonical_reference || record.reference || '');
    const displayRef = compact(record.display_reference || '');
    const brand = normalize(record.brand || '');
    const model = normalize(record.display_model || record.model || '');
    const haystack = normalize(watchSearchText(record));
    const hayCompact = compact(watchSearchText(record));
    let score = Number(record.display_priority || 0);

    if (qc && (ref === qc || displayRef === qc)) score += 20000;
    else if (qc && (ref.startsWith(qc) || displayRef.startsWith(qc))) score += 9000;
    else if (qc && hayCompact.includes(qc)) score += 4600;

    if (q && `${brand} ${model}` === q) score += 8000;
    else if (q && model.startsWith(q)) score += 4500;

    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length && tokens.every((token) => haystack.includes(token))) score += 3000;

    return score;
  }

  function watchResults(records, query, limit = 6) {
    const grouped = new Map();

    records.forEach((record) => {
      if (!String(record.verification_status || '').toUpperCase().startsWith('VERIFIED')) return;
      const score = scoreWatch(record, query);
      if (score <= 0) return;
      const width = Number(record.lug_width_mm || 0);
      const ref = String(record.display_reference || record.canonical_reference || record.reference || '').trim();
      const model = String(record.display_model || record.model || '').trim();
      const brand = String(record.brand || '').trim();
      const key = `${brand}|${model}|${ref}|${width}`.toLowerCase();
      const existing = grouped.get(key);
      if (!existing || score > existing.score) grouped.set(key, { record, score });
    });

    return Array.from(grouped.values())
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(({ record }) => {
        const width = Number(record.lug_width_mm || 0);
        const reference = String(record.canonical_reference || record.reference || record.display_reference || '').trim();
        const brand = String(record.brand || '').trim();
        const model = String(record.display_model || record.model || '').trim();
        return {
          type: 'WATCH',
          title: [brand, model].filter(Boolean).join(' '),
          subtitle: reference ? `Ref. ${record.display_reference || reference}` : window.DaLucaI18n.t("daluca_nav.verified_watch_fit", "Verified watch fit"),
          url: `${WATCH_FINDER_URL}?watch=${encodeURIComponent(reference)}`,
          meta: width ? `${width}mm${READY_WIDTHS.has(width) ? '' : ' · ' + window.DaLucaI18n.t("dl_pilot.psubmariner.custom_37", "Custom")}` : ''
        };
      });
  }

  function formatStorePrice(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: amount % 1 ? 2 : 0
      }).format(amount);
    } catch (_) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  async function shopifyPredictiveResults(query, signal) {
    if (normalize(query).length < 2) return [];
    const params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product,collection,page');
    params.set('resources[limit]', '8');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,tag,vendor,variants.title');

    try {
      const response = await fetch(window.DaLucaI18n.url(`/search/suggest.json?${params.toString()}`), {
        credentials: 'same-origin',
        signal
      });
      if (!response.ok) return [];
      const data = await response.json();
      const results = data?.resources?.results || {};
      const out = [];

      (results.products || []).slice(0, 5).forEach((item) => {
        const featured = item.featured_image && item.featured_image.url ? item.featured_image.url : '';
        out.push({
          type: 'PRODUCT',
          title: item.title || window.DaLucaI18n.t("sections.cart.headings.product", "Product"),
          subtitle: item.product_type || item.vendor || 'DaLuca',
          url: item.url || (item.handle ? `/products/${item.handle}` : '/search'),
          meta: formatStorePrice(item.price),
          image: featured || item.image || '',
          imageAlt: (item.featured_image && item.featured_image.alt) || item.title || window.DaLucaI18n.t("daluca_nav.daluca_product", "DaLuca product")
        });
      });

      (results.collections || []).slice(0, 4).forEach((item) => {
        out.push({
          type: 'COLLECTION',
          title: item.title || window.DaLucaI18n.t("daluca_nav.collection", "Collection"),
          subtitle: window.DaLucaI18n.t("daluca_nav.shop_collection", "Shop collection"),
          url: item.url || (item.handle ? `/collections/${item.handle}` : '/collections/all'),
          meta: '',
          image: item.featured_image && item.featured_image.url ? item.featured_image.url : '',
          imageAlt: (item.featured_image && item.featured_image.alt) || item.title || window.DaLucaI18n.t("daluca_nav.daluca_collection", "DaLuca collection")
        });
      });

      (results.pages || []).slice(0, 2).forEach((item) => {
        out.push({
          type: 'PAGE',
          title: item.title || window.DaLucaI18n.t("general.meta.page", "Page"),
          subtitle: 'DaLuca',
          url: item.url || '/pages/strap-finder',
          meta: ''
        });
      });
      return out;
    } catch (_) {
      return [];
    }
  }

  function dedupeResults(results, limit = 12) {
    const seen = new Set();
    const out = [];
    results.forEach((item) => {
      const key = String(item.url || `${item.type}|${item.title}`).toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(item);
    });
    return out.slice(0, limit);
  }

  function resultImage(item, className) {
    if (item.image) {
      return `<span class="${className}"><img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.imageAlt || item.title || '')}" loading="lazy"></span>`;
    }
    return `<span class="${className} ${className}--empty" aria-hidden="true"><span>DL</span></span>`;
  }

  function renderResults(root, query, results, statusText) {
    const box = root.querySelector('[data-dl-smart-results]');
    const input = root.querySelector('[data-dl-smart-input]');
    if (!box || !input) return;

    if (!query) {
      box.hidden = true;
      window.DaLucaI18n.setHtml(box, '');
      input.setAttribute('aria-expanded', 'false');
      return;
    }

    const watches = results.filter((item) => item.type === 'WATCH').slice(0, 2);
    const products = results.filter((item) => item.type === 'PRODUCT').slice(0, 4);
    const collections = results.filter((item) => item.type === 'COLLECTION').slice(0, 4);
    const pages = results.filter((item) => item.type === 'PAGE').slice(0, 2);

    const watchBlock = watches.length ? `
      <section class="dl-smart-search__fit-section" aria-label="${escapeHtml(window.DaLucaI18n.t("daluca_nav.exact_watch_fit", "Exact watch fit"))}">
        <div class="dl-smart-search__section-head"><span>${escapeHtml(window.DaLucaI18n.t("daluca_nav.exact_watch_fit", "Exact watch fit"))}</span><span>${escapeHtml(window.DaLucaI18n.t("daluca_nav.verified_database", "Verified database"))}</span></div>
        <div class="dl-smart-search__fit-grid">
          ${watches.map((item) => {
            const width = String(item.meta || '').split('·')[0].trim();
            return `<a class="dl-smart-search__fit-card dl-smart-search__result" href="${escapeHtml(window.DaLucaI18n.url(item.url))}">
              <span class="dl-smart-search__fit-badge">${escapeHtml(width || 'FIT')}</span>
              <span class="dl-smart-search__fit-copy">
                <strong>${escapeHtml(item.title)}</strong>
                <span>${escapeHtml(item.subtitle || '')}</span>
              </span>
              <span class="dl-smart-search__fit-action">${escapeHtml(window.DaLucaI18n.t("daluca_nav.use_exact_fit", "Use exact fit \u2192"))}</span>
            </a>`;
          }).join('')}
        </div>
      </section>` : '';

    const productBlock = products.length ? `
      <section class="dl-smart-search__column dl-smart-search__column--products">
        <div class="dl-smart-search__section-head"><span>${escapeHtml(window.DaLucaI18n.t("sections.header.products", "Products"))}</span><span>${products.length}</span></div>
        <div class="dl-smart-search__product-list">
          ${products.map((item) => `<a class="dl-smart-search__product dl-smart-search__result" href="${escapeHtml(window.DaLucaI18n.url(item.url))}">
            ${resultImage(item, 'dl-smart-search__thumb')}
            <span class="dl-smart-search__product-copy">
              <strong>${escapeHtml(item.title)}</strong>
              <span>${escapeHtml(item.subtitle || 'DaLuca')}</span>
            </span>
            <span class="dl-smart-search__price">${escapeHtml(item.meta || '')}</span>
          </a>`).join('')}
        </div>
      </section>` : '';

    const secondaryItems = [...collections, ...pages].slice(0, 5);
    const secondaryBlock = secondaryItems.length ? `
      <section class="dl-smart-search__column dl-smart-search__column--discover">
        <div class="dl-smart-search__section-head"><span>${escapeHtml(window.DaLucaI18n.t("daluca_nav.collections_pages", "Collections & pages"))}</span><span>${secondaryItems.length}</span></div>
        <div class="dl-smart-search__discover-list">
          ${secondaryItems.map((item) => `<a class="dl-smart-search__discover dl-smart-search__result" href="${escapeHtml(window.DaLucaI18n.url(item.url))}">
            ${item.type === 'COLLECTION' ? resultImage(item, 'dl-smart-search__mini-thumb') : '<span class="dl-smart-search__page-mark" aria-hidden="true">↗</span>'}
            <span class="dl-smart-search__discover-copy"><strong>${escapeHtml(item.title)}</strong><span>${escapeHtml(item.subtitle || item.type)}</span></span>
          </a>`).join('')}
        </div>
      </section>` : '';

    const emptyBlock = !watches.length && !products.length && !secondaryItems.length ? `
      <div class="dl-smart-search__empty">
        <strong>${escapeHtml(window.DaLucaI18n.t("daluca_nav.no_quick_match_yet", "No quick match yet."))}</strong>
        <span>${escapeHtml(window.DaLucaI18n.t("daluca_nav.press_enter_to_search_the_full_daluca_store_or_try_a_watch_referen", "Press Enter to search the full DaLuca store, or try a watch reference such as 124060."))}</span>
      </div>` : '';

    const fullSearchUrl = window.DaLucaI18n.url(`/search?q=${encodeURIComponent(query)}`);
    window.DaLucaI18n.setHtml(box, `
      <div class="dl-smart-search__status"><span>${escapeHtml(statusText || window.DaLucaI18n.t("daluca_nav.daluca_search", "DaLuca search"))}</span><span>${escapeHtml(query)}</span></div>
      ${watchBlock}
      <div class="dl-smart-search__results-grid">${productBlock}${secondaryBlock}</div>
      ${emptyBlock}
      <a class="dl-smart-search__all" href="${fullSearchUrl}"><span>${escapeHtml(window.DaLucaI18n.t("daluca_nav.search_all_daluca_results_for_query", "Search all DaLuca results for \u201c{{ query }}\u201d", { query }))}</span><span>→</span></a>`);
    box.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function createSearch(navIndex) {
    const root = document.createElement('div');
    root.className = 'dl-smart-search';
    root.setAttribute('data-dl-smart-search', '');
    window.DaLucaI18n.setHtml(root, `
      <div class="dl-smart-search__box">
        <span class="dl-smart-search__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="6.5"></circle><path d="M16 16l5 5"></path></svg>
        </span>
        <input class="dl-smart-search__input" data-dl-smart-input type="search" autocomplete="off" spellcheck="false" aria-label="${escapeHtml(window.DaLucaI18n.t("daluca_nav.search_daluca", "Search DaLuca"))}" aria-expanded="false" placeholder="${escapeHtml(window.DaLucaI18n.t("daluca_nav.search_watch_reference_product_material_or_size", "Search watch, reference, product, material or size..."))}">
        <button class="dl-smart-search__clear" data-dl-smart-clear type="button" aria-label="${escapeHtml(window.DaLucaI18n.t("daluca_nav.clear_search", "Clear search"))}" hidden>×</button>
        <span class="dl-smart-search__hint">${escapeHtml(window.DaLucaI18n.t("daluca_nav.ex_124060_20mm_shell", "Ex. 124060 \u00b7 20mm Shell"))}</span>
      </div>
      <div class="dl-smart-search__results" data-dl-smart-results hidden></div>`);

    const input = root.querySelector('[data-dl-smart-input]');
    const clearButton = root.querySelector('[data-dl-smart-clear]');
    let abortController = null;
    let keyboardIndex = -1;

    const runSearch = async () => {
      const query = input.value.trim();
      const seq = ++requestSequence;
      keyboardIndex = -1;

      if (!query) {
        renderResults(root, '', [], '');
        return;
      }

      const navMatches = localNavResults(navIndex, query, 5);
      renderResults(root, query, navMatches, window.DaLucaI18n.t("daluca_nav.searching_daluca", "Searching DaLuca"));

      if (abortController) abortController.abort();
      abortController = new AbortController();

      let watches = [];
      if (shouldSearchWatchDb(query)) {
        const db = await loadWatchDb();
        if (seq !== requestSequence) return;
        watches = watchResults(db, query, 6);
      }

      const predictive = await shopifyPredictiveResults(query, abortController.signal);
      if (seq !== requestSequence) return;

      const combined = dedupeResults([...watches, ...predictive, ...navMatches], 12);
      renderResults(root, query, combined, watches.length ? window.DaLucaI18n.t("daluca_nav.watch_fit_store_results", "Watch fit + store results") : window.DaLucaI18n.t("daluca_nav.daluca_results", "DaLuca results"));
    };

    input.addEventListener('input', () => {
      if (clearButton) clearButton.hidden = !input.value;
      window.clearTimeout(predictiveTimer);
      predictiveTimer = window.setTimeout(runSearch, 170);
    });

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        input.value = '';
        clearButton.hidden = true;
        renderResults(root, '', [], '');
        input.focus({ preventScroll: true });
      });
    }

    input.addEventListener('keydown', (event) => {
      const resultBox = root.querySelector('[data-dl-smart-results]');
      const links = resultBox ? Array.from(resultBox.querySelectorAll('.dl-smart-search__result')) : [];
      if (!links.length) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        keyboardIndex += event.key === 'ArrowDown' ? 1 : -1;
        if (keyboardIndex < 0) keyboardIndex = links.length - 1;
        if (keyboardIndex >= links.length) keyboardIndex = 0;
        links.forEach((link, index) => link.classList.toggle('is-keyboard-active', index === keyboardIndex));
        links[keyboardIndex].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const chosen = links[keyboardIndex >= 0 ? keyboardIndex : 0];
        if (chosen) window.location.assign(chosen.href);
        else if (input.value.trim()) window.location.assign(`/search?q=${encodeURIComponent(input.value.trim())}`);
      } else if (event.key === 'Escape') {
        const resultBoxNow = root.querySelector('[data-dl-smart-results]');
        if (resultBoxNow) resultBoxNow.hidden = true;
        input.setAttribute('aria-expanded', 'false');
      }
    });

    root.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!root.contains(document.activeElement)) {
          const resultBox = root.querySelector('[data-dl-smart-results]');
          if (resultBox) resultBox.hidden = true;
          input.setAttribute('aria-expanded', 'false');
        }
      }, 0);
    });

    return root;
  }

  function installSmartSearches() {
    const nav = document.querySelector('[data-dl-desktop-nav]');
    if (!nav) return;
    const navIndex = buildNavIndex(nav);

    nav.querySelectorAll('[data-dl-mega-panel] .dl-navsys').forEach((panelRoot) => {
      if (panelRoot.querySelector('[data-dl-smart-search]')) return;
      panelRoot.prepend(createSearch(navIndex));
    });
  }

  function focusSmartSearch(panel) {
    if (!panel) return;
    const input = panel.querySelector('[data-dl-smart-input]');
    if (input) window.setTimeout(() => input.focus({ preventScroll: true }), 40);
  }

  function connectHeaderSearchIcon() {
    const searchLink = document.getElementById('quick-search');
    const nav = document.querySelector('[data-dl-desktop-nav]');
    if (!searchLink || !nav) return;

    searchLink.addEventListener('click', (event) => {
      if (window.matchMedia('(max-width: 1067px)').matches) return;
      event.preventDefault();

      let openItem = nav.querySelector('[data-dl-nav-item].is-open');
      if (!openItem) {
        openItem = nav.querySelector('[data-nav-section="watch-straps"]');
        const trigger = openItem && openItem.querySelector('[data-dl-nav-trigger]');
        if (trigger) trigger.click();
      }

      const panel = openItem && openItem.querySelector('[data-dl-mega-panel]');
      focusSmartSearch(panel);
    });
  }

  function cleanupLegacyBottoms() {
    document.querySelectorAll('.dl-navsys__fit--empty').forEach((node) => {
      node.hidden = true;
    });
  }

  function initV11() {
    ensurePanelNotes();
    installSmartSearches();
    cleanupLegacyBottoms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initV11, { once: true });
  } else {
    initV11();
  }
})();


/* =========================================================
   DALUCA NAV V12 — UTILITY ORDER + NATIVE SEARCH COMPARISON
   Accessories stays with shopping categories; Strap Finder is the
   final utility item. Native Shopify search remains available from
   the header icon so the two search systems can be compared.
   ========================================================= */
(() => {
  'use strict';

  function reorderMobileFinder() {
    const finderLink = document.querySelector('.dl-mobile-menu-drawer a.dl-mobile-direct--finder');
    const finderItem = finderLink && finderLink.closest('li');
    if (!finderItem || !finderItem.parentElement) return;

    const siblings = Array.from(finderItem.parentElement.children);
    const accessoriesItem = siblings.find((li) => {
      const summary = li.querySelector(':scope > details > summary');
      return summary && summary.getAttribute('data-dl-mobile-section') === 'accessories';
    });

    if (accessoriesItem && accessoriesItem.nextElementSibling !== finderItem) {
      finderItem.parentElement.insertBefore(accessoriesItem, finderItem);
    }
  }

  function initV12Order() {
    reorderMobileFinder();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initV12Order, { once: true });
  } else {
    initV12Order();
  }
})();

/* =========================================================
   DA LUCA MEGA NAV V50 — THUMBNAIL + PANEL GEOMETRY FOUNDATION
   Production cleanup of the old V26 controller.
   This block now ONLY:
   - builds the visual thumbnails used by the approved card system
   - keeps the fixed mega-panel top aligned to the desktop navigation
   Connector drawing and selection are owned exclusively by the V45 controller below.
   ========================================================= */
(() => {
  'use strict';

  const MQ = '(min-width: 1068px)';
  let geoRaf = 0;

  const spriteMap = {
    'dl-v20-img--two-piece':[0,0], 'dl-v20-img--single-piece':[1,0], 'dl-v20-img--strap-apple':[2,0], 'dl-v20-img--perlon':[3,0],
    'dl-v20-img--rubber':[4,0], 'dl-v20-img--custom':[5,0], 'dl-v20-img--nylon':[6,0], 'dl-v20-img--leather':[7,0],
    'dl-v20-img--vintage':[0,1], 'dl-v20-img--shell':[1,1], 'dl-v20-img--suede':[2,1], 'dl-v20-img--mat-rubber':[3,1],
    'dl-v20-img--custom-leather':[4,1], 'dl-v20-img--custom-vintage':[5,1], 'dl-v20-img--custom-shell':[6,1], 'dl-v20-img--custom-alligator':[7,1],
    'dl-v20-img--custom-lizard':[0,2], 'dl-v20-img--belts':[1,2], 'dl-v20-img--alligator-belts':[2,2], 'dl-v20-img--crocodile-belts':[3,2],
    'dl-v20-img--lizard-belts':[4,2], 'dl-v20-img--wallets':[5,2], 'dl-v20-img--travel-cases':[6,2], 'dl-v20-img--key-chains':[7,2],
    'dl-v20-img--leather-care':[0,3], 'dl-v20-img--tools':[1,3], 'dl-v20-img--the-ball':[2,3], 'dl-v20-img--the-cuff':[3,3],
    'dl-v20-img--the-case':[4,3], 'dl-v20-img--the-tray':[5,3], 'dl-v20-img--rolex-datejust':[6,3], 'dl-v20-img--rolex-daytona':[7,3],
    'dl-v20-img--rolex-explorer':[0,4], 'dl-v20-img--rolex-explorer2':[1,4], 'dl-v20-img--rolex-gmt':[2,4], 'dl-v20-img--rolex-submariner':[3,4],
    'dl-v20-img--panerai-luminor':[4,4], 'dl-v20-img--panerai-luminor-due':[5,4], 'dl-v20-img--panerai-radiomir':[6,4], 'dl-v20-img--panerai-submersible':[7,4],
    'dl-v20-img--omega-seamaster':[0,5], 'dl-v20-img--omega-speedmaster':[1,5], 'dl-v20-img--tudor-blackbay':[2,5], 'dl-v20-img--tudor-pelagos':[3,5],
    'dl-v20-img--iwc-pilot':[4,5], 'dl-v20-img--iwc-portofino':[5,5], 'dl-v20-img--iwc-portugieser':[6,5], 'dl-v20-img--apple-watch':[7,5]
  };

  const cleanQikifyMap = {
    'dl-v20-img--rolex-datejust':[0,0], 'dl-v20-img--rolex-daytona':[1,0], 'dl-v20-img--rolex-explorer':[2,0], 'dl-v20-img--rolex-explorer2':[3,0],
    'dl-v20-img--rolex-gmt':[4,0], 'dl-v20-img--rolex-submariner':[5,0], 'dl-v20-img--panerai-luminor':[6,0], 'dl-v20-img--panerai-luminor-due':[7,0],
    'dl-v20-img--panerai-radiomir':[0,1], 'dl-v20-img--panerai-submersible':[1,1], 'dl-v20-img--omega-seamaster':[2,1], 'dl-v20-img--omega-speedmaster':[3,1],
    'dl-v20-img--tudor-blackbay':[4,1], 'dl-v20-img--tudor-pelagos':[5,1], 'dl-v20-img--iwc-pilot':[6,1], 'dl-v20-img--iwc-portofino':[7,1],
    'dl-v20-img--iwc-portugieser':[0,2], 'dl-v20-img--apple-watch':[1,2],
    'dl-v20-img--belts':[2,2], 'dl-v20-img--alligator-belts':[3,2], 'dl-v20-img--crocodile-belts':[4,2], 'dl-v20-img--lizard-belts':[5,2],
    'dl-v20-img--wallets':[6,2], 'dl-v20-img--travel-cases':[7,2], 'dl-v20-img--key-chains':[0,3], 'dl-v20-img--leather-care':[1,3],
    'dl-v20-img--tools':[2,3], 'dl-v20-img--the-ball':[3,3], 'dl-v20-img--the-cuff':[4,3], 'dl-v20-img--the-case':[5,3], 'dl-v20-img--the-tray':[6,3]
  };

  // Full watches return to their original white-background photographs.
  const photosV030 = {
    "dl-v20-img--the-cuff": "https://dalucastraps.com/cdn/shop/files/dalucacufffulleditedSQUARE.jpg?v=1788458866&width=640",
    "dl-v20-img--two-piece": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/3c7a9616af858097399bcb73783809d7f2786bc0d9a266b426ebe2f5dfde3313.jpeg",
    "dl-v20-img--single-piece": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/61f1f5eb2931f458c38bc498537914b196ea06338b074c3b34dd892822323ed1.jpeg",
    "dl-v20-img--strap-apple": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/ed82f96b9be24d93e472be4795bf7b15ac26cd1d4291e9b8ee87048ec66ff8cc.jpeg",
    "dl-v20-img--perlon": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/c42d529eb75b2bfb39682bf1e10c985807c9901db256bce4a41639f43c304cb0.jpeg",
    "dl-v20-img--rubber": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/bd651d1c56c9a97f682ea792704f80c8ab180751b4ca6e5789059128a0a57b58.jpeg",
    "dl-v20-img--custom": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/9e7fee94b5d0b2ad8a58e6ed9fb8532002f084d95a90d9f2036756f160512afd.jpeg",
    "dl-v20-img--nylon": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/4bd24bdb6815c01c0aaaab22e09ce272bed7bdcaca97739bf71cb4f50d14afb6.jpeg",
    "dl-v20-img--leather": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/c5cc33325fd38f84f00ab2d66bb8f1cabd1f7b2b9eec18be8295a1a9b050e835.jpeg",
    "dl-v20-img--vintage": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/77403487949ea2b04705ac093803ae95ef270333bbddafd3a768fbe41cfd3c0e.jpeg",
    "dl-v20-img--shell": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/5c8a4fcb1bd494c00ab939da74fedb71b1d8ec50fa484dd7d9a6b33275826287.jpeg",
    "dl-v20-img--suede": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/ec5001e496c5f899bb50eabbfe37852de421d9f2e6da816d5beb6292437f772c.jpeg",
    "dl-v20-img--mat-rubber": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/99fbb90f675707259a402a95a5064740b97e50425dc546ec49266b0eeb207563.jpeg",
    "dl-v20-img--custom-leather": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/a77797a10961125e553411bac431036deca54b08060f521fb94b46603ae63c5f.jpeg",
    "dl-v20-img--custom-vintage": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/0d003aa56d9ee0153a6d9ab03f2ea64cf34930b54a546bec38fc5f591048c61f.jpeg",
    "dl-v20-img--custom-shell": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/6de9fe836f44272182c4d750058ffec2ba06a211fd5cacc5bc271192cbc08616.jpeg",
    "dl-v20-img--custom-alligator": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/ad35c7d0cdde423cb1fe60a35f595c5d584c47a7918b73762934a2fe7c8ff332.jpeg",
    "dl-v20-img--custom-lizard": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/bd254834f57e5232f66d3f17b9d3efaab9c042001a11fa9792018bd6ddb606b8.jpeg",
    "dl-v20-img--belts": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/99c7a372aead532298b447c489d68e08548282b2fd6df25c883ee1d31e9e01ee.jpeg",
    "dl-v20-img--alligator-belts": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/71df918059b6465c0ccef9bb35ee255516be260e8871c840d78b839a81633104.jpeg",
    "dl-v20-img--crocodile-belts": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/a99cf13e09d1b6ff7eeb770d70ff4cfffe949a71ef6a946f10bd940014dbe35a.jpeg",
    "dl-v20-img--lizard-belts": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/c942ac3f6de8ef5f3cd3b39b51fe54dd008209f32b2640241bb1e94d375b2a46.jpeg",
    "dl-v20-img--wallets": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/90657b2eac3a6995a880582b28290d5703b78f7558976e52c1d421f9418893b3.jpeg",
    "dl-v20-img--travel-cases": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/c566eb6bb65fd80542b90b2a4a474f381da77ae3ac900551e5039b7a7fc28f71.jpeg",
    "dl-v20-img--key-chains": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/5ab674288eedd73136e5ebf22f1df66c1b3495414ab7fb7bfe7fe4fd372c5f8c.jpeg",
    "dl-v20-img--leather-care": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/62ec33ea6f99368fdb64650d016e9d106ec4006273bbb583bc0c6004e7688424.jpeg",
    "dl-v20-img--tools": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/afec8cda0d57c853814425adaa3128131c9e4619c2a2f0b53576e401df1f8b2f.jpeg",
    "dl-v20-img--the-ball": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/6c83cf66af8011bcac4611194900aec27e0521be59b43ef1da999dd6ff3876a4.jpeg",
    "dl-v20-img--the-case": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/b88030f65f5adb73199cbba15a222cad7de7dfe15f8e38066b234a1d8b93acd6.jpeg",
    "dl-v20-img--the-tray": "https://qikify-cdn.nyc3.digitaloceanspaces.com/production/tmenu/instances/276800/9719fb444c4a4542c990abb4aca9b92c43276ed1ac0a0724c35ce820c20f661a.jpeg"
};


  const isDesktop = () => window.matchMedia(MQ).matches;

  function imageKey(link) {
    const source = link && link.querySelector('.dl-v20-hover-preview .dl-v20-preview-image');
    if (!source) return null;
    return Array.from(source.classList).find((name) => spriteMap[name]) || null;
  }

  function resolvedSpriteBackground(link) {
    const source = link && link.querySelector('.dl-v20-hover-preview .dl-v20-preview-image');
    if (source) {
      const background = window.getComputedStyle(source).backgroundImage;
      if (background && background !== 'none') return background;
    }
    const nav = document.querySelector('[data-dl-desktop-nav]');
    if (!nav) return '';
    return window.getComputedStyle(nav).getPropertyValue('--dl-v20-sprite').trim();
  }

  function resolvedNavVariable(name) {
    const nav = document.querySelector('[data-dl-desktop-nav]');
    return nav ? window.getComputedStyle(nav).getPropertyValue(name).trim() : '';
  }

  function installThumb(link) {
    if (!link || link.querySelector(':scope > .dl-v26-thumb')) return;
    const key = imageKey(link);
    if (!key) return;
    const [col,row] = spriteMap[key];
    const thumb = document.createElement('span');
    thumb.className = 'dl-v26-thumb';
    thumb.dataset.dlPhotoKey = key;
    thumb.style.setProperty('--dl-photo-col', col);thumb.style.setProperty('--dl-photo-row', row);
    thumb.setAttribute('aria-hidden','true');
    const image = document.createElement('span');
    image.className = 'dl-v26-thumb__image';

    const cleanCell = /rolex-|panerai-|omega-|tudor-|iwc-|apple-watch/.test(key) ? cleanQikifyMap[key] : null;
    const watchPhoto = photosV030[key];
    if (watchPhoto) {
      image.classList.add('is-dl-v30-photo');if(cleanCell)image.classList.add('is-dl-v30-watch');
      const photo = document.createElement('img');
      photo.src = watchPhoto;
      photo.dataset.dlWatchImage = key;
      photo.alt = '';
      photo.width = cleanCell ? 80 : 400;
      photo.height = cleanCell ? 80 : 267;
      photo.loading = 'lazy';
      photo.decoding = 'async';
      photo.addEventListener('error', () => {
        image.classList.remove('is-dl-v30-photo','is-dl-v30-watch');
        photo.remove();
        if (cleanCell) {
          image.classList.add('is-dl-v29-qikify-clean');
          image.style.setProperty('background-image', resolvedNavVariable('--dl-v29-qikify-clean'), 'important');
          image.style.setProperty('--dl-v29-qx', `${-cleanCell[0]*96}px`);
          image.style.setProperty('--dl-v29-qy', `${-cleanCell[1]*96}px`);
        }
      }, { once: true });
      image.appendChild(photo);
    } else if (cleanCell) {
      image.classList.add('is-dl-v29-qikify-clean');
      const cleanBackground = resolvedNavVariable('--dl-v29-qikify-clean');
      if (cleanBackground) image.style.setProperty('background-image', cleanBackground, 'important');
      image.style.setProperty('--dl-v29-qx', `${-cleanCell[0]*96}px`);
      image.style.setProperty('--dl-v29-qy', `${-cleanCell[1]*96}px`);
    } else {
      const spriteBackground = resolvedSpriteBackground(link);
      if (spriteBackground) image.style.setProperty('background-image', spriteBackground, 'important');
    }

    image.style.setProperty('--dl-v26-x', `${-col*168}px`);
    image.style.setProperty('--dl-v26-y', `${-row*112}px`);
    image.style.setProperty('--dl-v26-sx', `${-col*156}px`);
    image.style.setProperty('--dl-v26-sy', `${-row*104}px`);
    image.style.setProperty('--dl-v26-gx', `${-col*150}px`);
    image.style.setProperty('--dl-v26-gy', `${-row*100}px`);
    image.style.setProperty('--dl-v26-mx', `${-col*140}px`);
    image.style.setProperty('--dl-v26-my', `${-row*(280/3)}px`);
    thumb.appendChild(image);
    const text = link.querySelector(':scope > .dl-v20-link-text');
    link.insertBefore(thumb, text || link.firstChild);
  }

  function installThumbs(root=document) {
    root.querySelectorAll('.dl-v20-link').forEach(installThumb);
  }

  function geometry() {
    geoRaf = 0;
    if (!isDesktop()) return;
    const header = document.querySelector('.header') || document.querySelector('.dl-header-shell');
    if (!header) return;
    const desktopNav = document.querySelector('[data-dl-desktop-nav]');
    const anchorRect = desktopNav ? desktopNav.getBoundingClientRect() : header.getBoundingClientRect();
    const bottom = Math.max(0,Math.round(anchorRect.bottom - 1));
    document.documentElement.style.setProperty('--dl-mega-fixed-top',`${bottom}px`);

    const master = document.querySelector('[data-dl-desktop-nav] .dl-nav-master-toggle');
    if (master) ['left','right','top','transform','width','height'].forEach((p) => master.style.removeProperty(p));
    document.querySelectorAll('[data-dl-mega-panel]').forEach((panel) => {
      ['left','right','width','max-width','height','min-height'].forEach((p) => panel.style.removeProperty(p));
    });
  }

  function scheduleGeometry() {
    if (geoRaf) return;
    geoRaf = requestAnimationFrame(geometry);
  }

  function init() {
    installThumbs();
    geometry();
    const nav = document.querySelector('[data-dl-desktop-nav]');
    if (nav) {
      nav.addEventListener('click',() => requestAnimationFrame(() => {
        installThumbs(nav);
        scheduleGeometry();
      }),true);
    }
    window.addEventListener('resize',scheduleGeometry,{passive:true});
    window.addEventListener('scroll',scheduleGeometry,{passive:true});
    window.addEventListener('pageshow',scheduleGeometry,{passive:true});
    if ('ResizeObserver' in window) {
      const header = document.querySelector('.header') || document.querySelector('.dl-header-shell');
      if (header) new ResizeObserver(scheduleGeometry).observe(header);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',init,{once:true});
  else init();
})();

/* =========================================================
   DALUCA NAV V45 — SINGLE CONNECTOR CONTROLLER
   - Historical V36/V44 connector controllers removed so they cannot redraw
     old L markers, helper labels, or ghost routes after this controller runs.
   - Straight segments only: horizontal -> diagonal chamfer -> horizontal.
   - The route starts from the user-approved point to the right of the active
     By Type / By Material / By Width label, never directly off the text.
   - 1st option TOP, 2nd BOTTOM, then strict alternating order.
   - BOTTOM routes clear both the image and its full text label.
   - No L/corner marker. The selected visual box itself gets the dark border.
   - Production status lives at the far right of the width tab rail.
   ========================================================= */
(() => {
  'use strict';

  const MQ='(min-width:1068px)';
  const READY_WIDTHS=new Set([18,20,22,24,26]);
  let raf=0;
  const desktop=()=>window.matchMedia(MQ).matches;

  function activePane(stage){
    return stage && (
      stage.querySelector('.dl-v20-pane.is-active:not([hidden])') ||
      stage.querySelector('.dl-v20-pane:not([hidden])')
    );
  }

  function activeRail(stage){
    return stage && stage.querySelector(
      '.dl-v20-rail-btn.is-active, .dl-v20-rail-btn[aria-selected="true"]'
    );
  }

  function activeWidthPane(pane){
    return pane && (
      pane.querySelector('.dl-v20-width-pane.is-active:not([hidden])') ||
      pane.querySelector('.dl-v20-width-pane:not([hidden])')
    );
  }

  function cardRoot(pane){
    if(!pane) return null;
    return pane.matches('[data-dl-switcher-pane="width"]')
      ? (activeWidthPane(pane) || pane)
      : pane;
  }

  function visibleCards(root){
    return root
      ? Array.from(root.querySelectorAll('.dl-v20-link')).filter(node=>node.offsetParent!==null)
      : [];
  }

  function ensureSelection(root){
    if(!root) return null;
    let selected=root.querySelector('.dl-v20-link.is-dl-v26-selected');
    if(selected && selected.offsetParent!==null) return selected;
    root.querySelectorAll('.dl-v20-link.is-dl-v26-selected').forEach(node=>node.classList.remove('is-dl-v26-selected'));
    selected=visibleCards(root)[0] || null;
    if(selected) selected.classList.add('is-dl-v26-selected');
    return selected;
  }

  function choose(link){
    if(!link) return;
    const pane=link.closest('.dl-v20-pane');
    const root=cardRoot(pane);
    if(!root || !root.contains(link)) return;
    root.querySelectorAll('.dl-v20-link.is-dl-v26-selected').forEach(node=>node.classList.remove('is-dl-v26-selected'));
    link.classList.add('is-dl-v26-selected');
  }

  function visibleTextRect(element){
    if(!element) return null;
    const label=element.querySelector('span') || element;
    const walker=document.createTreeWalker(label,NodeFilter.SHOW_TEXT,{
      acceptNode(node){
        return node.nodeValue && node.nodeValue.trim()
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      }
    });
    const rects=[];
    let node;
    while((node=walker.nextNode())){
      const range=document.createRange();
      range.selectNodeContents(node);
      const r=range.getBoundingClientRect();
      if(r.width>0 && r.height>0) rects.push(r);
    }
    if(!rects.length) return label.getBoundingClientRect();
    const left=Math.min(...rects.map(r=>r.left));
    const right=Math.max(...rects.map(r=>r.right));
    const top=Math.min(...rects.map(r=>r.top));
    const bottom=Math.max(...rects.map(r=>r.bottom));
    return {left,right,top,bottom,width:right-left,height:bottom-top};
  }

  function cleanupHistorical(stage){
    if(!stage) return;
    stage.querySelectorAll(
      ':scope > .dl-v39-connector,'+
      ':scope > .dl-v26-connector,'+
      ':scope > .dl-v25-connector,'+
      ':scope > .dl-v24-connector,'+
      ':scope > .dl-v23-connector,'+
      ':scope > .dl-v22-connector'
    ).forEach(node=>node.remove());

  }

  function ensureConnector(stage){
    let svg=stage.querySelector(':scope > .dl-v45-connector');
    if(svg) return svg;
    svg=document.createElementNS('http://www.w3.org/2000/svg','svg');
    svg.classList.add('dl-v45-connector');
    svg.setAttribute('aria-hidden','true');
    const route=document.createElementNS('http://www.w3.org/2000/svg','polyline');
    route.classList.add('dl-v45-route');
    svg.append(route);
    stage.prepend(svg);
    return svg;
  }

  function sameVisualRow(cards,target){
    const targetThumb=target.querySelector('.dl-v26-thumb');
    if(!targetThumb) return [target];
    const tr=targetThumb.getBoundingClientRect();
    const targetMid=tr.top+tr.height/2;
    return cards.filter(card=>{
      const thumb=card.querySelector('.dl-v26-thumb');
      if(!thumb) return false;
      const r=thumb.getBoundingClientRect();
      return Math.abs((r.top+r.height/2)-targetMid)<Math.max(34,r.height*.42);
    });
  }

  function pointsAttr(points){
    return points.map(([x,y])=>`${Number(x).toFixed(1)},${Number(y).toFixed(1)}`).join(' ');
  }

  function draw(stage){
    if(!desktop() || !stage) return;
    cleanupHistorical(stage);

    const pane=activePane(stage);
    const root=cardRoot(pane);
    const from=activeRail(stage);
    const selected=ensureSelection(root);
    const svg=ensureConnector(stage);
    const route=svg.querySelector('.dl-v45-route');

    if(!pane || !root || !from || !selected || from.offsetParent===null || selected.offsetParent===null){
      route.setAttribute('points','');
      return;
    }

    const cards=visibleCards(root);
    const index=cards.indexOf(selected);
    const thumb=selected.querySelector('.dl-v26-thumb');
    if(index<0 || !thumb){
      route.setAttribute('points','');
      return;
    }

    const stageRect=stage.getBoundingClientRect();
    const categoryRect=visibleTextRect(from) || from.getBoundingClientRect();
    const thumbRect=thumb.getBoundingClientRect();
    const rowCards=sameVisualRow(cards,selected);

    const rowThumbRects=rowCards.map(card=>{
      const t=card.querySelector('.dl-v26-thumb');
      return t ? t.getBoundingClientRect() : card.getBoundingClientRect();
    });
    const rowLabelRects=rowCards.map(card=>{
      const l=card.querySelector('.dl-v20-link-text');
      return l ? l.getBoundingClientRect() : card.getBoundingClientRect();
    });

    const allThumbRects=cards.map(card=>{
      const t=card.querySelector('.dl-v26-thumb');
      return t ? t.getBoundingClientRect() : card.getBoundingClientRect();
    });

    const gridLeft=Math.min(...allThumbRects.map(r=>r.left-stageRect.left));
    const targetLeft=thumbRect.left-stageRect.left;
    const rowTop=Math.min(...rowThumbRects.map(r=>r.top-stageRect.top));
    const rowLabelBottom=Math.max(...rowLabelRects.map(r=>r.bottom-stageRect.top));

    /* Strict alternating destination lane: 1 top / 2 bottom / 3 top / 4 bottom. */
    const footer=Array.from(stage.querySelectorAll('.dl-v45-pane-footer,.dl-v45-stage-footer')).find(node=>node.offsetParent!==null);
    const footerTop=footer ? footer.getBoundingClientRect().top-stageRect.top : stageRect.height;
    const isTop=index%2===0 || rowLabelBottom+14>footerTop-12;
    const laneY=isTop ? rowTop-12 : rowLabelBottom+14;

    /* USER-APPROVED LAUNCH POINT:
       start noticeably to the right of the category wording, exactly like the
       blue annotation. It never grows a tiny preliminary stub off the text. */
    const textRight=categoryRect.right-stageRect.left;
    const startX=Math.min(gridLeft-145,textRight+44);
    const startY=categoryRect.top-stageRect.top+categoryRect.height/2;

    /* One substantial straight lead, one diagonal chamfer, then the final rail.
       All elevation change is finished before card #1. */
    const gateX=gridLeft-26;
    const leadEndX=Math.min(gateX-58,startX+66);
    const endX=targetLeft-7; /* consistent 7px air gap before the selected box */

    const points=[
      [startX,startY],
      [Math.max(startX+48,leadEndX),startY],
      [gateX,laneY],
      [endX,laneY]
    ];

    svg.setAttribute('viewBox',`0 0 ${Math.max(1,stage.clientWidth)} ${Math.max(1,stage.scrollHeight)}`);
    route.setAttribute('points',pointsAttr(points));
    svg.dataset.side=isTop?'top':'bottom';
  }

  function isShopAll(link){
    return !!link && link.hasAttribute('data-dl-shop-all');
  }

  function normalizeShopAll(){
    document.querySelectorAll('.dl-v20-width-pane').forEach(pane=>{
      const head=pane.querySelector(':scope > .dl-v20-width-head');
      const link=head && Array.from(head.querySelectorAll('a')).find(isShopAll);
      let footer=pane.querySelector(':scope > .dl-v45-pane-footer, :scope > .dl-v39-pane-footer');
      if(link && !footer){
        footer=document.createElement('div');
        footer.className='dl-v45-pane-footer';
        pane.appendChild(footer);
      }
      if(link && footer){
        link.classList.add('dl-v45-view-all');
        if(link.parentElement !== footer) footer.appendChild(link);
      }
    });

    document.querySelectorAll('.dl-navsys--v20').forEach(nav=>{
      const topLine=nav.querySelector(':scope > .dl-v20-topline');
      const link=topLine && Array.from(topLine.querySelectorAll('a')).find(isShopAll);
      const stage=nav.querySelector(':scope > .dl-v20-stage');
      if(!link || !stage) return;
      let footer=stage.querySelector(':scope > .dl-v45-stage-footer, :scope > .dl-v39-stage-footer');
      if(!footer){
        footer=document.createElement('div');
        footer.className='dl-v45-stage-footer';
        stage.appendChild(footer);
      }
      link.classList.add('dl-v45-view-all');
      if(link.parentElement !== footer) footer.appendChild(link);
    });
  }

  function currentWidth(pane){
    const active=pane.querySelector('.dl-v20-width-tabs button.is-active, .dl-v20-width-tabs button[aria-selected="true"]');
    if(!active) return null;
    const width = active.getAttribute('data-dl-width') || active.getAttribute('data-dl-sub-tab');
    return /^(18|19|20|21|22|23|24|25|26)$/.test(width || '') ? Number(width) : null;
  }

  function normalizeWidthStatus(){
    /* Historical status nodes are obsolete. The approved V45 status is now
       reused in place so geometry refreshes do not churn the DOM. */
    document.querySelectorAll('.dl-v39-width-status').forEach(node=>node.remove());

    document.querySelectorAll('.dl-v20-pane[data-dl-switcher-pane="width"]').forEach(pane=>{
      const tabs=pane.querySelector('.dl-v20-width-tabs');
      const width=currentWidth(pane);
      if(!tabs || width==null) return;

      const existing=Array.from(tabs.querySelectorAll(':scope > .dl-v45-width-status'));
      let status=existing.shift();
      existing.forEach(node=>node.remove());

      if(!status){
        status=document.createElement('span');
        status.className='dl-v45-width-status';
        status.setAttribute('aria-hidden','true');
        tabs.appendChild(status);
      }

      const next=READY_WIDTHS.has(width) ? window.DaLucaI18n.t("daluca_nav.ready_made_61930a", "READY MADE") : window.DaLucaI18n.t("daluca_nav.made_to_order_775eac", "MADE TO ORDER");
      if(window.DaLucaI18n.sourceText(status)!==next) window.DaLucaI18n.setText(status, next);
    });
  }

  function organizeCustomWatchBrands(){
    document.querySelectorAll('[data-dl-switcher-pane="custom-watch"]').forEach(pane=>{
      if(pane.dataset.dlBrandBrowser) return;
      const list=pane.querySelector('.dl-v20-group-list');
      const groups=list && Array.from(list.querySelectorAll(':scope > .dl-v20-group'));
      if(!groups || groups.length<2)return;
      pane.dataset.dlBrandBrowser='true';
      const tabs=document.createElement('div');tabs.className='dl-v37-brand-tabs';
      const controls=[];
      groups.forEach((group,index)=>{
        const heading=group.querySelector('b');
        const button=document.createElement('button');button.type='button';window.DaLucaI18n.setText(button, window.DaLucaI18n.chainText(() => (heading), [{kind:"text", optional:true}]) || '');
        button.setAttribute('aria-pressed',String(index===0));
        group.hidden=index!==0;group.dataset.dlBrandCount=String(group.querySelectorAll('.dl-v20-link').length);
        button.addEventListener('click',()=>{groups.forEach((item,i)=>{item.hidden=i!==index;controls[i].setAttribute('aria-pressed',String(i===index));});schedule();});
        controls.push(button);tabs.appendChild(button);
      });
      list.before(tabs);
    });
  }
  function schedule(){
    cancelAnimationFrame(raf);
    raf=requestAnimationFrame(()=>{
      normalizeShopAll();
      normalizeWidthStatus();
      organizeCustomWatchBrands();
      document.querySelectorAll('.dl-v20-stage').forEach(draw);
    });
  }

  document.addEventListener('mouseover',event=>{
    const link=event.target.closest('.dl-v20-link');
    if(!link || !desktop()) return;
    choose(link);
    schedule();
  },true);

  document.addEventListener('focusin',event=>{
    const link=event.target.closest('.dl-v20-link');
    if(link && desktop()) choose(link);
    if(event.target.closest('.dl-v20-link,.dl-v20-rail-btn,.dl-v20-width-tabs button')) schedule();
  },true);

  document.addEventListener('click',event=>{
    if(event.target.closest('.dl-v20-rail-btn,.dl-v20-width-tabs button,.dl-nav-trigger,.dl-nav-master-toggle')){
      setTimeout(schedule,0);
      setTimeout(schedule,90);
    }
  },true);

  document.addEventListener('daluca:mega-opened',()=>{
    setTimeout(schedule,0);
    setTimeout(schedule,90);
  });

  window.addEventListener('resize',schedule,{passive:true});
  window.addEventListener('pageshow',schedule,{passive:true});

  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',schedule,{once:true});
  else schedule();
})();

/* ================================================================
   DA LUCA MEGA NAV V52 — MOBILE / DYNAMIC DOM HARDENING
   Preserves the approved V49 layout. This controller only reduces unnecessary
   whole-document rescans and adds reliable Escape-key closing on mobile.
   ================================================================ */
(() => {
  'use strict';

  const MOBILE_MQ = '(max-width:1067px)';
  const CHAT_SELECTORS = [
    '#ShopifyChat',
    '#shopify-chat',
    '.shopify-chat',
    '#chat-widget-container',
    '#gorgias-chat-container',
    '.gorgias-chat-key',
    '#tidio-chat',
    '.crisp-client',
    'iframe[title*="chat" i]',
    'iframe[src*="chat" i]',
    'iframe[src*="inbox" i]'
  ].join(',');

  function removeDuplicateMobileHead(root = document) {
    if (root.nodeType === 1 && root.matches && root.matches('.dl-mobile-nav-head')) {
      root.remove();
      return;
    }
    if (root.querySelectorAll) root.querySelectorAll('.dl-mobile-nav-head').forEach((node) => node.remove());
  }

  function markSevenCardList(list) {
    if (!list || !list.classList || !list.classList.contains('dl-v20-list')) return;
    const directCards = Array.from(list.children).filter((child) =>
      child.classList && child.classList.contains('dl-v20-link')
    );
    list.classList.toggle('dl-v49-count-7', directCards.length === 7);
  }

  function markSevenCardLists(root = document) {
    if (root.nodeType === 1 && root.matches && root.matches('.dl-navsys--v20 .dl-v20-list')) {
      markSevenCardList(root);
    }
    if (root.querySelectorAll) {
      root.querySelectorAll('.dl-navsys--v20 .dl-v20-list').forEach(markSevenCardList);
    }
  }

  function mobileMenuIsOpen() {
    const wrapper = document.querySelector('.dl-mobile-toggle-wrapper');
    if (!wrapper || !window.matchMedia(MOBILE_MQ).matches) return false;
    return wrapper.hasAttribute('open') || wrapper.getAttribute('aria-expanded') === 'true';
  }

  function syncChatVisibility(open, root = document) {
    document.documentElement.classList.toggle('dl-mobile-nav-open', open);
    if (document.body) document.body.classList.toggle('dl-mobile-nav-open', open);

    const nodes = [];
    if (root.nodeType === 1 && root.matches && root.matches(CHAT_SELECTORS)) nodes.push(root);
    if (root.querySelectorAll) nodes.push(...root.querySelectorAll(CHAT_SELECTORS));

    nodes.forEach((node) => {
      if (open) {
        if (!node.hasAttribute('data-dl-v49-chat-hidden')) {
          node.setAttribute('data-dl-v49-chat-hidden', '');
          node.dataset.dlV49OldVisibility = node.style.visibility || '';
          node.dataset.dlV49OldOpacity = node.style.opacity || '';
          node.dataset.dlV49OldPointerEvents = node.style.pointerEvents || '';
          node.dataset.dlV49OldVisibilityPriority = node.style.getPropertyPriority('visibility') || '';
          node.dataset.dlV49OldOpacityPriority = node.style.getPropertyPriority('opacity') || '';
          node.dataset.dlV49OldPointerEventsPriority = node.style.getPropertyPriority('pointer-events') || '';
        }
        node.style.setProperty('visibility', 'hidden', 'important');
        node.style.setProperty('opacity', '0', 'important');
        node.style.setProperty('pointer-events', 'none', 'important');
      } else if (node.hasAttribute('data-dl-v49-chat-hidden')) {
        node.style.setProperty('visibility', node.dataset.dlV49OldVisibility || '', node.dataset.dlV49OldVisibilityPriority || '');
        node.style.setProperty('opacity', node.dataset.dlV49OldOpacity || '', node.dataset.dlV49OldOpacityPriority || '');
        node.style.setProperty('pointer-events', node.dataset.dlV49OldPointerEvents || '', node.dataset.dlV49OldPointerEventsPriority || '');
        node.removeAttribute('data-dl-v49-chat-hidden');
        delete node.dataset.dlV49OldVisibility;
        delete node.dataset.dlV49OldOpacity;
        delete node.dataset.dlV49OldPointerEvents;
        delete node.dataset.dlV49OldVisibilityPriority;
        delete node.dataset.dlV49OldOpacityPriority;
        delete node.dataset.dlV49OldPointerEventsPriority;
      }
    });
  }

  function syncMobileState() {
    removeDuplicateMobileHead();
    syncChatVisibility(mobileMenuIsOpen());
  }

  function closeMobileMenuFromKeyboard() {
    const wrapper = document.querySelector('.dl-mobile-toggle-wrapper');
    if (!wrapper || !mobileMenuIsOpen()) return false;
    wrapper.removeAttribute('open');
    wrapper.setAttribute('aria-expanded', 'false');
    const summary = wrapper.querySelector('summary');
    if (summary) summary.focus({ preventScroll: true });
    syncMobileState();
    return true;
  }

  function addedNodeNeedsChatSync(node) {
    if (!node || node.nodeType !== 1) return false;
    return !!(
      (node.matches && node.matches(CHAT_SELECTORS)) ||
      (node.querySelector && node.querySelector(CHAT_SELECTORS))
    );
  }

  function initV52DynamicHardening() {
    removeDuplicateMobileHead();
    markSevenCardLists();
    syncMobileState();

    const wrapper = document.querySelector('.dl-mobile-toggle-wrapper');
    if (wrapper) {
      new MutationObserver(syncMobileState).observe(wrapper, {
        attributes: true,
        attributeFilter: ['open', 'aria-expanded', 'class']
      });
    }

    /* Theme/Qikify/app code can inject menu or chat markup late. Process only
       the added subtree instead of rescanning the entire document every time. */
    new MutationObserver((mutations) => {
      const open = mobileMenuIsOpen();
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!node || node.nodeType !== 1) return;
          removeDuplicateMobileHead(node);
          markSevenCardLists(node);
          const parentList = node.parentElement && node.parentElement.closest('.dl-navsys--v20 .dl-v20-list');
          if (parentList) markSevenCardList(parentList);
          if (open && addedNodeNeedsChatSync(node)) syncChatVisibility(true, node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.dl-header-shell, .dl-mobile-menu-drawer')) return;
      requestAnimationFrame(() => {
        markSevenCardLists();
        syncMobileState();
      });
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !window.matchMedia(MOBILE_MQ).matches) return;
      if (closeMobileMenuFromKeyboard()) event.preventDefault();
    });

    window.addEventListener('resize', syncMobileState, { passive: true });
    window.addEventListener('pageshow', () => {
      markSevenCardLists();
      syncMobileState();
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initV52DynamicHardening, { once: true });
  } else {
    initV52DynamicHardening();
  }
})();

/* DA LUCA MEGA NAV V50 — PRODUCTION CONSOLIDATED.
   Approved V49 behavior retained; obsolete duplicate connector controller removed. */

/* ================================================================
   DA LUCA MEGA NAV V51/V52 — CLEARANCE LABEL NORMALIZATION
   Replaces the legacy "Last Chance" wording with one simple "Clearance"
   label. V52 processes only newly inserted subtrees after initial load.
   ================================================================ */
(() => {
  'use strict';

  const ROOT_SELECTOR = '.dl-header-shell, .dl-mobile-menu-drawer';
  const ITEM_SELECTOR = 'a[href], button, summary';
  const SCOPED_ITEM_SELECTOR = '.dl-header-shell a[href], .dl-header-shell button, .dl-header-shell summary, .dl-mobile-menu-drawer a[href], .dl-mobile-menu-drawer button, .dl-mobile-menu-drawer summary';
  const EXACT_CLEARANCE = /^(?:clearance(?:\s*(?:\/|\||·|—|–|-)\s*last\s+chance)?|last\s+chance(?:\s*(?:\/|\||·|—|–|-)\s*clearance)?)$/i;
  const TERM = /clearance|last\s+chance/i;
  const SEPARATOR_ONLY = /^[\s\/|·—–-]+$/;

  function clean(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function normalizeItem(item) {
    if (!item || item.dataset.dlV51Clearance === '1') return;

    const label = clean(window.DaLucaI18n.sourceText(item));
    if (item.hasAttribute('data-dl-clearance-item')) return;
    if (!EXACT_CLEARANCE.test(label)) return;

    const walker = document.createTreeWalker(item, NodeFilter.SHOW_TEXT);
    const relevant = [];
    const punctuation = [];
    let node;

    while ((node = walker.nextNode())) {
      const text = clean(node.nodeValue);
      if (!text) continue;
      if (TERM.test(text)) relevant.push(node);
      else if (SEPARATOR_ONLY.test(text)) punctuation.push(node);
    }

    if (!relevant.length) return;

    const first = relevant.shift();
    const span = document.createElement('span');
    span.className = 'dl-nav-clearance-label';
    window.DaLucaI18n.setText(span, window.DaLucaI18n.t("daluca_nav.clearance", "Clearance"));
    first.parentNode.replaceChild(span, first);

    relevant.forEach((textNode) => { textNode.nodeValue = ''; });
    punctuation.forEach((textNode) => { textNode.nodeValue = ''; });

    item.dataset.dlV51Clearance = '1';
    item.setAttribute('data-dl-clearance-item', '');
  }

  function normalizeWithin(root) {
    if (!root || root.nodeType !== 1) return;

    if (root.matches && root.matches(SCOPED_ITEM_SELECTOR)) normalizeItem(root);

    if (root.matches && root.matches(ROOT_SELECTOR)) {
      root.querySelectorAll(ITEM_SELECTOR).forEach(normalizeItem);
      return;
    }

    if (!root.querySelectorAll) return;
    root.querySelectorAll(ROOT_SELECTOR).forEach((navRoot) => {
      navRoot.querySelectorAll(ITEM_SELECTOR).forEach(normalizeItem);
    });
    root.querySelectorAll(SCOPED_ITEM_SELECTOR).forEach(normalizeItem);
  }

  function normalizeClearance() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      root.querySelectorAll(ITEM_SELECTOR).forEach(normalizeItem);
    });
  }

  function initV52Clearance() {
    normalizeClearance();

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node && node.nodeType === 1) normalizeWithin(node);
        });
      });
    }).observe(document.documentElement, { childList:true, subtree:true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initV52Clearance, { once:true });
  } else {
    initV52Clearance();
  }
})();

